import * as apicall from "../components/Apicall";
import { EMPTY_HR_ACCESS } from "../constants/hrPermissions";

// HRMS API client - same pattern as inventoryService.js / expenseService.js.
// Every path is under /api/v1/hrms (the base URL already ends at api/v1/).
// Writes are POST (create-or-update); apicall has no put(). Ids are the encrypted
// strings the API returned - pass them back untouched.
//
// apicall.get throws on a non-2xx answer; apicall.post resolves with
// { success:false, message } (or express-validator's { errors:[{msg}] }).

export const responseMessage = (res, fallback = "Something went wrong") => res?.message || res?.errors?.[0]?.msg || fallback;

// Unwraps a { success, data } answer, or throws the API's own message.
export const unwrap = (res) => {
  if (res && res.success === false) throw new Error(responseMessage(res));
  return res?.data;
};

const qs = (params = {}) => {
  const p = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  return p.length ? "?" + p.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&") : "";
};

/* ---------------------------------------------------------------- *
 *  Access - drives the HR sidebar group and route guards
 * ---------------------------------------------------------------- */
export const getAccess = async () => {
  try {
    const res = await apicall.get("hrms/access");
    return res?.data || EMPTY_HR_ACCESS;
  } catch (err) {
    // Not enabled / not signed in / API down: treat as "no HR", never break the shell.
    return EMPTY_HR_ACCESS;
  }
};

/* ---------------------------------------------------------------- *
 *  Settings (country pack, timezone, employee code prefix, ...)
 * ---------------------------------------------------------------- */
export const getSettings = () => apicall.get("hrms/settings");
export const saveSettings = (data) => apicall.post(data, "hrms/settings");

/* ---------------------------------------------------------------- *
 *  Background jobs - poll until status is DONE or FAILED
 * ---------------------------------------------------------------- */
export const getJob = (id) => apicall.get(`hrms/jobs/${id}`);

/* ---------------------------------------------------------------- *
 *  Super Admin - switch add-ons (PAYROLL / EXPENSES / SCHEDULE_BRIDGE)
 *  on for one vendor. `vendorId` is the encrypted id the API returned.
 * ---------------------------------------------------------------- */
export const setFeatureEnabled = (vendorId, featureKey, isEnabled) =>
  apicall.post({ feature_key: featureKey, is_enabled: isEnabled }, `hrms/admin/enablement/${vendorId}`);
export const getEnablement = (vendorId) => apicall.get(`hrms/admin/enablement/${vendorId}`);

/* ---------------------------------------------------------------- *
 *  Organisation masters: departments, designations, grades,
 *  locations, sites, cost-centres
 * ---------------------------------------------------------------- */
export const listMaster = (key, params) => apicall.get(`hrms/${key}${qs(params)}`);
export const saveMaster = (key, id, data) => apicall.post(data, id ? `hrms/${key}/${id}` : `hrms/${key}`);
export const deactivateMaster = (key, id) => apicall.del(`hrms/${key}/${id}`);

/* ---------------------------------------------------------------- *
 *  Permission grants
 * ---------------------------------------------------------------- */
export const listGrants = () => apicall.get("hrms/permissions");
export const grantPermission = (employeeId, permission) => apicall.post({ employee_id: employeeId, permission }, "hrms/permissions");
export const revokePermission = (id) => apicall.del(`hrms/permissions/${id}`);

/* ---------------------------------------------------------------- *
 *  Employees
 * ---------------------------------------------------------------- */
export const listEmployees = (params) => apicall.get(`hrms/employees${qs(params)}`);
export const getDirectory = (params) => apicall.get(`hrms/directory${qs(params)}`);
export const getOrgChart = (rootId) => apicall.get(`hrms/org-chart${qs({ root_id: rootId })}`);
export const getEmployeeCandidates = () => apicall.get("hrms/employees/candidates");
export const getMyProfile = () => apicall.get("hrms/employees/me");
export const getEmployee = (id) => apicall.get(`hrms/employees/${id}`);
export const createEmployee = (data) => apicall.post(data, "hrms/employees");
export const enableEmployee = (userId, data) => apicall.post(data, `hrms/employees/enable/${userId}`);
export const updateEmployee = (id, data) => apicall.post(data, `hrms/employees/${id}`);
// Employees tier (plans other than the HR plan): ends employment today and switches the login off.
// Support <-> HR only, owner / Admin only (see RoleControl). role: 6 | 7
export const changeEmployeeRole = (id, role) => apicall.post({ role }, `hrms/employees/${id}/change-role`);
export const endEmployment = (id) => apicall.post({}, `hrms/employees/${id}/end-employment`);
export const getEmployeeHistory = (id) => apicall.get(`hrms/employees/${id}/history`);
export const addEmployeeEvent = (id, data) => apicall.post(data, `hrms/employees/${id}/events`);
export const correctEmployeeHistory = (id, historyId, changes) => apicall.post(changes, `hrms/employees/${id}/history/${historyId}`);
export const saveStatutory = (id, data) => apicall.post(data, `hrms/employees/${id}/statutory`);
export const requestBankChange = (id, data) => apicall.post(data, `hrms/employees/${id}/bank`);
export const requestProfileChange = (id, data) => apicall.post(data, `hrms/employees/${id}/profile-change`);
export const listEmployeeChanges = (id) => apicall.get(`hrms/employees/${id}/changes`);
export const saveEmployeeChild = (id, kind, childId, data) => apicall.post(data, childId ? `hrms/employees/${id}/${kind}/${childId}` : `hrms/employees/${id}/${kind}`);
export const deleteEmployeeChild = (id, kind, childId) => apicall.del(`hrms/employees/${id}/${kind}/${childId}`);
export const listCustomFields = (all) => apicall.get(`hrms/custom-fields${qs({ all: all ? 1 : undefined })}`);
export const saveCustomField = (id, data) => apicall.post(data, id ? `hrms/custom-fields/${id}` : "hrms/custom-fields");
export const setCustomValues = (id, values) => apicall.post({ values }, `hrms/employees/${id}/custom-fields`);
export const getImportTemplate = () => apicall.get("hrms/employees/import/template");
export const previewImport = (rows) => apicall.post({ rows }, "hrms/employees/import/preview");
export const commitImport = (rows) => apicall.post({ rows }, "hrms/employees/import");

/* ---------------------------------------------------------------- *
 *  Separation
 * ---------------------------------------------------------------- */
export const listSeparations = (params) => apicall.get(`hrms/separations${qs(params)}`);
export const getSeparation = (id) => apicall.get(`hrms/separations/${id}`);
export const requestSeparation = (data) => apicall.post(data, "hrms/separations");
export const withdrawSeparation = (id) => apicall.post({}, `hrms/separations/${id}/withdraw`);
export const cancelSeparation = (id) => apicall.post({}, `hrms/separations/${id}/cancel`);
export const saveExitNotes = (id, notes) => apicall.post({ notes }, `hrms/separations/${id}/exit-notes`);

/* ---------------------------------------------------------------- *
 *  Approvals (workflow engine) and delegation
 * ---------------------------------------------------------------- */
export const getInbox = (history) => apicall.get(`hrms/approvals/inbox${qs({ history: history ? 1 : undefined })}`);
export const getApprovalCount = () => apicall.get("hrms/approvals/count");
export const getMyRequests = () => apicall.get("hrms/approvals/mine");
export const getApproval = (id) => apicall.get(`hrms/approvals/${id}`);
export const actOnApproval = (id, action, comment) => apicall.post({ action, comment }, `hrms/approvals/${id}/action`);
export const withdrawApproval = (id) => apicall.post({}, `hrms/approvals/${id}/withdraw`);
export const bulkApprovalAction = (requestIds, action, comment) => apicall.post({ request_ids: requestIds, action, comment }, "hrms/approvals/bulk-action");
export const listDelegations = () => apicall.get("hrms/delegations");
export const createDelegation = (data) => apicall.post(data, "hrms/delegations");
export const cancelDelegation = (id) => apicall.del(`hrms/delegations/${id}`);

export const listWorkflows = (requestType) => apicall.get(`hrms/workflows${qs({ request_type: requestType })}`);
export const saveWorkflow = (id, data) => apicall.post(data, id ? `hrms/workflows/${id}` : "hrms/workflows");
export const deactivateWorkflow = (id) => apicall.del(`hrms/workflows/${id}`);

/* ---------------------------------------------------------------- *
 *  Holidays
 * ---------------------------------------------------------------- */
export const listCalendars = (params) => apicall.get(`hrms/holiday-calendars${qs(params)}`);
export const getCalendar = (id) => apicall.get(`hrms/holiday-calendars/${id}`);
export const saveCalendar = (id, data) => apicall.post(data, id ? `hrms/holiday-calendars/${id}` : "hrms/holiday-calendars");
export const copyCalendar = (id, data) => apicall.post(data, `hrms/holiday-calendars/${id}/copy`);
export const listHolidayTemplates = () => apicall.get("hrms/holiday-templates");
export const calendarFromTemplate = (data) => apicall.post(data, "hrms/holiday-calendars/from-template");
export const addHoliday = (calendarId, data) => apicall.post(data, `hrms/holiday-calendars/${calendarId}/holidays`);
export const updateHoliday = (id, data) => apicall.post(data, `hrms/holidays/${id}`);
export const removeHoliday = (id) => apicall.del(`hrms/holidays/${id}`);
export const assignCalendar = (calendarId, data) => apicall.post(data, `hrms/holiday-calendars/${calendarId}/assignments`);
export const unassignCalendar = (calendarId, assignmentId) => apicall.del(`hrms/holiday-calendars/${calendarId}/assignments/${assignmentId}`);
export const getMyHolidays = (year) => apicall.get(`hrms/holidays/mine${qs({ year })}`);

/* ---------------------------------------------------------------- *
 *  Leave
 * ---------------------------------------------------------------- */
export const listLeaveTypes = (all) => apicall.get(`hrms/leave-types${qs({ all: all ? 1 : undefined })}`);
export const saveLeaveType = (id, data) => apicall.post(data, id ? `hrms/leave-types/${id}` : "hrms/leave-types");
export const listLeavePolicies = () => apicall.get("hrms/leave-policies");
export const getLeavePolicy = (id) => apicall.get(`hrms/leave-policies/${id}`);
export const saveLeavePolicy = (id, data) => apicall.post(data, id ? `hrms/leave-policies/${id}` : "hrms/leave-policies");
export const assignLeavePolicy = (id, data) => apicall.post(data, `hrms/leave-policies/${id}/assign`);
export const applyLeaveTemplate = (data) => apicall.post(data, "hrms/leave/templates/apply");
export const previewLeave = (data) => apicall.post(data, "hrms/leave/preview");
export const applyLeave = (data) => apicall.post(data, "hrms/leave-requests");
export const listLeaveRequests = (params) => apicall.get(`hrms/leave-requests${qs(params)}`);
export const getLeaveRequest = (id) => apicall.get(`hrms/leave-requests/${id}`);
export const resubmitLeave = (id, data) => apicall.post(data, `hrms/leave-requests/${id}/resubmit`);
export const withdrawLeave = (id) => apicall.post({}, `hrms/leave-requests/${id}/withdraw`);
export const cancelLeave = (id, data) => apicall.post(data || {}, `hrms/leave-requests/${id}/cancel`);
export const getLeaveAttachmentUrl = (id) => apicall.get(`hrms/leave-requests/${id}/attachment`);
export const uploadLeaveAttachment = (file, employeeId) => {
  const form = new FormData();
  form.append("file", file);
  if (employeeId) form.append("employee_id", employeeId);
  return apicall.post(form, "hrms/leave/attachment", true);
};
export const claimCompOff = (data) => apicall.post(data, "hrms/comp-off");
export const getLeaveBalance = (params) => apicall.get(`hrms/leave/balance${qs(params)}`);
export const getLeaveLedger = (params) => apicall.get(`hrms/leave/ledger${qs(params)}`);
export const adjustLeave = (data) => apicall.post(data, "hrms/leave/adjust");
export const getLeaveCalendar = (from, to) => apicall.get(`hrms/leave/calendar${qs({ from, to })}`);
export const getWhosAway = (date) => apicall.get(`hrms/leave/whos-away${qs({ date })}`);

/* ---------------------------------------------------------------- *
 *  Documents, company documents, letters
 * ---------------------------------------------------------------- */
export const listDocumentTypes = (all) => apicall.get(`hrms/document-types${qs({ all: all ? 1 : undefined })}`);
export const saveDocumentType = (id, data) => apicall.post(data, id ? `hrms/document-types/${id}` : "hrms/document-types");
export const getEmployeeDocuments = (id) => apicall.get(`hrms/employees/${id}/documents`);
export const uploadEmployeeDocument = (id, file, fields) => {
  const form = new FormData();
  form.append("file", file);
  Object.entries(fields || {}).forEach(([k, v]) => v !== undefined && v !== null && v !== "" && form.append(k, v));
  return apicall.post(form, `hrms/employees/${id}/documents`, true);
};
export const getEmployeeDocumentUrl = (id) => apicall.get(`hrms/employee-documents/${id}/url`);
export const verifyEmployeeDocument = (id, status, comment) => apicall.post({ status, comment }, `hrms/employee-documents/${id}/verify`);
export const getExpiringDocuments = (days) => apicall.get(`hrms/documents/expiring${qs({ days })}`);

export const listCompanyDocuments = () => apicall.get("hrms/company-documents");
export const uploadCompanyDocument = (file, fields) => {
  const form = new FormData();
  form.append("file", file);
  Object.entries(fields || {}).forEach(([k, v]) => v !== undefined && v !== null && v !== "" && form.append(k, v));
  return apicall.post(form, "hrms/company-documents", true);
};
export const getCompanyDocumentUrl = (id) => apicall.get(`hrms/company-documents/${id}/url`);
export const setCompanyDocumentPublished = (id, published) => apicall.post({}, `hrms/company-documents/${id}/${published ? "publish" : "unpublish"}`);
export const acknowledgeCompanyDocument = (id) => apicall.post({}, `hrms/company-documents/${id}/acknowledge`);
export const getAcknowledgements = (id) => apicall.get(`hrms/company-documents/${id}/acknowledgements`);

export const listLetterTemplates = (params) => apicall.get(`hrms/letter-templates${qs(params)}`);
export const saveLetterTemplate = (id, data) => apicall.post(data, id ? `hrms/letter-templates/${id}` : "hrms/letter-templates");
export const generateLetter = (data) => apicall.post(data, "hrms/letters/generate");
export const requestLetter = (data) => apicall.post(data, "hrms/letters/request");
export const listLetters = (employeeId) => apicall.get(`hrms/letters${qs({ employee_id: employeeId })}`);
export const getLetterUrl = (id) => apicall.get(`hrms/letters/${id}/url`);

/* ---------------------------------------------------------------- *
 *  Time: shifts, patterns, roster, site assignments, swaps
 * ---------------------------------------------------------------- */
export const listShifts = (all) => apicall.get(`hrms/shifts${qs({ all: all ? 1 : undefined })}`);
export const saveShift = (id, data) => apicall.post(data, id ? `hrms/shifts/${id}` : "hrms/shifts");
export const listPatterns = (all) => apicall.get(`hrms/shift-patterns${qs({ all: all ? 1 : undefined })}`);
export const savePattern = (id, data) => apicall.post(data, id ? `hrms/shift-patterns/${id}` : "hrms/shift-patterns");
export const listPatternAssignments = (employeeId) => apicall.get(`hrms/pattern-assignments${qs({ employee_id: employeeId })}`);
export const assignPattern = (data) => apicall.post(data, "hrms/pattern-assignments");
export const endPatternAssignment = (id, endDate) => apicall.post({ end_date: endDate }, `hrms/pattern-assignments/${id}/end`);
export const getRoster = (params) => apicall.get(`hrms/roster${qs(params)}`);
export const getMyRoster = (from, to) => apicall.get(`hrms/roster/mine${qs({ from, to })}`);
export const getRosterVsActual = (params) => apicall.get(`hrms/roster/vs-actual${qs(params)}`);
export const generateRoster = (data) => apicall.post(data, "hrms/roster/generate");
export const previewRosterEntries = (data) => apicall.post(data, "hrms/roster/entries/preview");
export const saveRosterEntries = (data) => apicall.post(data, "hrms/roster/entries");
export const clearRoster = (data) => apicall.post(data, "hrms/roster/clear");
export const publishRoster = (data) => apicall.post(data, "hrms/roster/publish");
export const listSiteAssignments = (params) => apicall.get(`hrms/site-assignments${qs(params)}`);
export const assignSite = (data) => apicall.post(data, "hrms/site-assignments");
export const endSiteAssignment = (id, toDate) => apicall.post({ to_date: toDate }, `hrms/site-assignments/${id}/end`);
export const listSwaps = (all) => apicall.get(`hrms/shift-swaps${qs({ all: all ? 1 : undefined })}`);
export const requestSwap = (data) => apicall.post(data, "hrms/shift-swaps");
export const respondToSwap = (id, accept) => apicall.post({ accept }, `hrms/shift-swaps/${id}/respond`);
export const withdrawSwap = (id) => apicall.post({}, `hrms/shift-swaps/${id}/withdraw`);

/* ---------------------------------------------------------------- *
 *  Attendance, corrections, absence, overtime
 * ---------------------------------------------------------------- */
export const punch = (data) => apicall.post(data, "hrms/attendance/punch");
export const getPunchToday = () => apicall.get("hrms/attendance/today");
export const getMyAttendance = (from, to) => apicall.get(`hrms/attendance/my${qs({ from, to })}`);
export const getPersonAttendance = (employeeId, from, to) => apicall.get(`hrms/attendance/person${qs({ employee_id: employeeId, from, to })}`);
export const getRawPunches = (employeeId, from, to) => apicall.get(`hrms/attendance/punches${qs({ employee_id: employeeId, from, to })}`);
export const getLivePunches = (date) => apicall.get(`hrms/attendance/live${qs({ date })}`);
export const getMuster = (params) => apicall.get(`hrms/attendance/muster${qs(params)}`);
export const getTeamToday = () => apicall.get("hrms/attendance/team-today");
export const getAttendanceLock = () => apicall.get("hrms/attendance/lock");
export const lockAttendance = (lockDate, note) => apicall.post({ lock_date: lockDate, note }, "hrms/attendance/lock");
export const getAttendanceLockHistory = () => apicall.get("hrms/attendance/lock/history");
export const reprocessAttendance = (data) => apicall.post(data, "hrms/attendance/reprocess");
export const getMarkSheet = (date) => apicall.get(`hrms/attendance/mark/sheet${qs({ date })}`);
export const saveMarks = (data) => apicall.post(data, "hrms/attendance/mark");
export const listAttendancePolicies = () => apicall.get("hrms/attendance/policies");
export const saveAttendancePolicy = (id, data) => apicall.post(data, id ? `hrms/attendance/policies/${id}` : "hrms/attendance/policies");
export const requestCorrection = (data) => apicall.post(data, "hrms/regularisations");
export const listCorrections = (params) => apicall.get(`hrms/regularisations${qs(params)}`);
export const listAbsences = (params) => apicall.get(`hrms/absences${qs(params)}`);
export const getAbsenceAnalytics = (from, to) => apicall.get(`hrms/absences/analytics${qs({ from, to })}`);
export const excuseAbsence = (id, note) => apicall.post({ note }, `hrms/absences/${id}/excuse`);
export const listAbscondingAlerts = (status) => apicall.get(`hrms/absconding-alerts${qs({ status })}`);
export const closeAbscondingAlert = (id, status) => apicall.post({ status }, `hrms/absconding-alerts/${id}/close`);
export const getOtPolicy = () => apicall.get("hrms/ot/policy");
export const saveOtPolicy = (data) => apicall.post(data, "hrms/ot/policy");
export const listOvertime = (params) => apicall.get(`hrms/ot${qs(params)}`);
export const requestOvertime = (data) => apicall.post(data, "hrms/ot/request");
export const getOtReport = (from, to) => apicall.get(`hrms/ot/report${qs({ from, to })}`);

/* ---------------------------------------------------------------- *
 *  Schedule bridge: one-time import of the booking shifts
 * ---------------------------------------------------------------- */
export const previewShiftImport = (startDate) => apicall.get(`hrms/bridge/shift-import/preview${qs({ start_date: startDate })}`);
export const commitShiftImport = (startDate) => apicall.post({ start_date: startDate }, "hrms/bridge/shift-import");

/* ---------------------------------------------------------------- *
 *  Payroll: setup, salaries, one-time items, runs, payslips
 * ---------------------------------------------------------------- */
export const listPayComponents = (all) => apicall.get(`hrms/payroll/components${qs({ all: all ? 1 : undefined })}`);
export const savePayComponent = (id, data) => apicall.post(data, id ? `hrms/payroll/components/${id}` : "hrms/payroll/components");
export const seedPayComponents = () => apicall.post({}, "hrms/payroll/components/seed-starter");
export const listSalaryTemplates = () => apicall.get("hrms/payroll/templates");
export const getSalaryTemplate = (id) => apicall.get(`hrms/payroll/templates/${id}`);
export const saveSalaryTemplate = (id, data) => apicall.post(data, id ? `hrms/payroll/templates/${id}` : "hrms/payroll/templates");
export const getPayrollSettings = () => apicall.get("hrms/payroll/settings");
export const savePayrollSettings = (data) => apicall.post(data, "hrms/payroll/settings");
export const getTenantStatutoryRates = () => apicall.get("hrms/payroll/statutory-rates");
export const getSalaryHistory = (employeeId) => apicall.get(`hrms/payroll/employees/${employeeId}/salary`);
export const setSalary = (employeeId, data) => apicall.post(data, `hrms/payroll/employees/${employeeId}/salary`);
export const listPayrollInputs = (params) => apicall.get(`hrms/payroll/inputs${qs(params)}`);
export const createPayrollInput = (data) => apicall.post(data, "hrms/payroll/inputs");
export const voidPayrollInput = (id) => apicall.post({}, `hrms/payroll/inputs/${id}/void`);

export const listPayrollRuns = (params) => apicall.get(`hrms/payroll/runs${qs(params)}`);
export const createPayrollRun = (data) => apicall.post(data, "hrms/payroll/runs");
export const createFnfRun = (data) => apicall.post(data, "hrms/payroll/runs/fnf");
export const getPayrollRun = (id) => apicall.get(`hrms/payroll/runs/${id}`);
export const saveFnfParams = (id, data) => apicall.post(data, `hrms/payroll/runs/${id}/fnf-params`);
export const calculatePayrollRun = (id) => apicall.post({}, `hrms/payroll/runs/${id}/calculate`);
export const listRunEmployees = (id, params) => apicall.get(`hrms/payroll/runs/${id}/employees${qs(params)}`);
export const getRunEmployee = (id, employeeId) => apicall.get(`hrms/payroll/runs/${id}/employee${qs({ employee_id: employeeId })}`);
export const getRunRegister = (id) => apicall.get(`hrms/payroll/runs/${id}/register`);
export const getRunContributions = (id) => apicall.get(`hrms/payroll/runs/${id}/contributions`);
export const holdPayment = (id, employeeId, reason) => apicall.post({ employee_id: employeeId, reason }, `hrms/payroll/runs/${id}/hold`);
export const releasePayment = (id, employeeId) => apicall.post({ employee_id: employeeId }, `hrms/payroll/runs/${id}/release`);
export const submitPayrollRun = (id) => apicall.post({}, `hrms/payroll/runs/${id}/submit`);
export const approvePayrollRun = (id) => apicall.post({}, `hrms/payroll/runs/${id}/approve`);
export const rejectPayrollRun = (id, reason) => apicall.post({ reason }, `hrms/payroll/runs/${id}/reject`);
export const withdrawPayrollRun = (id) => apicall.post({}, `hrms/payroll/runs/${id}/withdraw`);
export const lockPayrollRun = (id) => apicall.post({}, `hrms/payroll/runs/${id}/lock`);
export const publishPayrollRun = (id) => apicall.post({}, `hrms/payroll/runs/${id}/publish`);
export const cancelPayrollRun = (id, reason) => apicall.post({ reason }, `hrms/payroll/runs/${id}/cancel`);
export const retryPayslips = (id) => apicall.post({}, `hrms/payroll/runs/${id}/payslips/retry`);
export const getEosbProvision = (month) => apicall.get(`hrms/payroll/reports/eosb-provision${qs({ month })}`);

export const listMyPayslips = () => apicall.get("hrms/payslips");
export const getPayslip = (id) => apicall.get(`hrms/payslips/${id}`);
export const getPayslipDownload = (id) => apicall.get(`hrms/payslips/${id}/download`);

// Super Admin: the statutory master data every tenant's payroll reads.
export const listStatutoryRates = (country) => apicall.get(`hrms/admin/statutory-rates${qs({ country })}`);
export const setStatutoryRate = (data) => apicall.post(data, "hrms/admin/statutory-rates");
export const seedStatutoryDefaults = () => apicall.post({}, "hrms/admin/statutory-rates/seed-defaults");

/* ---------------------------------------------------------------- *
 *  Attendance logs exported from a device and uploaded by hand
 * ---------------------------------------------------------------- */
export const previewDeviceLogs = (data) => apicall.post(data, "hrms/attendance/device-logs/preview");
export const commitDeviceLogs = (data) => apicall.post(data, "hrms/attendance/device-logs");
export const listDeviceLogImports = () => apicall.get("hrms/attendance/device-logs/imports");
export const listDeviceUsers = () => apicall.get("hrms/attendance/device-users");
export const removeDeviceUser = (id) => apicall.del(`hrms/attendance/device-users/${id}`);
